import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { EyeIcon, CodeBracketIcon, CheckIcon, ExclamationTriangleIcon, ArrowLeftIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { HelpIcon } from './HelpIcon';
import { TooltipProvider } from './ui/tooltip';
import { getHelpContent } from '../lib/helpContent';
import { Template, TemplateEditorProps } from '../types/template';
import { TemplateVariablesInfo } from './Templates/TemplateVariablesInfo';
import { EmailEditor } from './Templates/EmailEditor';

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  templateId: templateIdProp,
  onSave,
  onCancel,
  onSuccess,
  isModal = false
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id: templateIdParam } = useParams<{ id: string }>();
  
  // Use templateId from props if provided, otherwise from URL params
  const templateId = templateIdProp || templateIdParam;
  const [template, setTemplate] = useState<Partial<Template>>({
    name: '',
    description: '',
    template_type: 'email',
    status: 'draft',
    subject: '',
    content: '',
    variables: [],
    preview_data: {}
  });

  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState<Record<string, any>>({});
  const [validation, setValidation] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Fetch template if editing
  const { data: existingTemplate, isLoading: isLoadingTemplate } = useQuery<Template>({
    queryKey: ['template', templateId],
    queryFn: async () => {
      const response = await api.templates.get(templateId!);
      return response.data;
    },
    enabled: !!templateId
  });

  // Update template when existing template loads
  useEffect(() => {
    if (existingTemplate) {
      setTemplate(existingTemplate);
      setPreviewData(existingTemplate.preview_data || {});
    }
  }, [existingTemplate]);

  // Validate template
  const validateTemplate = (): boolean => {
    const errors: string[] = [];
    
    if (!template.name || template.name.trim() === '') {
      errors.push('Template name is required');
    }
    
    if (!template.content || template.content.trim() === '') {
      errors.push('Template content is required');
    }
    
    if (template.template_type === 'email' && (!template.subject || template.subject.trim() === '')) {
      errors.push('Email subject is required');
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Save template mutation
  const saveTemplateMutation = useMutation({
    mutationFn: async (templateData: Partial<Template>) => {
      if (templateId) {
        const response = await api.templates.update(templateId, templateData);
        return response.data;
      } else {
        const response = await api.templates.create(templateData);
        return response.data;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      
      // Show success toast
      toast.success(templateId ? 'Template updated successfully' : 'Template created successfully');
      
      // Call callbacks
      onSave?.(data);
      onSuccess?.(data);
      
      // Redirect to templates list if not in modal mode
      if (!isModal && !templateId) {
        navigate('/templates');
      } else if (!isModal && templateId) {
        navigate('/templates');
      }
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save template';
      toast.error(errorMessage);
      
      // Extract validation errors if available
      if (error.response?.data?.errors) {
        setValidationErrors(error.response.data.errors);
      }
    },
  });

  // Preview template mutation
  const previewMutation = useMutation({
    mutationFn: async (variables: Record<string, any>) => {
      const response = await api.templates.preview(templateId!, variables);
      return response.data;
    },
  });

  // Validate template mutation
  const validateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.templates.validate(templateId!);
      return response.data;
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.templates.duplicate(templateId!);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template duplicated successfully');
      // Navigate to edit the new template
      navigate(`/templates/${data.id}/edit`);
    },
    onError: (error: any) => {
      toast.error(`Failed to duplicate template: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleSave = () => {
    // Clear previous errors
    setValidationErrors([]);
    
    // Validate before saving
    if (!validateTemplate()) {
      toast.error('Please fix the validation errors');
      return;
    }
    
    saveTemplateMutation.mutate(template);
  };
  
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (!isModal) {
      navigate('/templates');
    }
  };
  
  const handleSaveAsCopy = () => {
    if (templateId) {
      duplicateMutation.mutate();
    }
  };

  const handlePreview = () => {
    if (templateId) {
      // For saved templates, fetch preview from server
      previewMutation.mutate(previewData);
    }
    // For unsaved templates, client-side preview will be used
    setPreviewMode(!previewMode);
  };

  const handleValidate = () => {
    if (templateId) {
      validateMutation.mutate();
    }
  };

  const extractVariables = (content: string) => {
    const variablePattern = /\{\{([^}]+)\}\}/g;
    const matches = content.match(variablePattern);
    return matches ? matches.map(match => match.replace(/[{}]/g, '')) : [];
  };

  const handleContentChange = (content: string) => {
    const variables = extractVariables(content);
    setTemplate(prev => ({
      ...prev,
      content,
      variables
    }));
  };

  const handleSubjectChange = (subject: string) => {
    const subjectVariables = extractVariables(subject);
    const contentVariables = extractVariables(template.content || '');
    const allVariables = [...new Set([...subjectVariables, ...contentVariables])];
    
    setTemplate(prev => ({
      ...prev,
      subject,
      variables: allVariables
    }));
  };

  // Client-side preview renderer for unsaved templates
  const renderClientPreview = () => {
    const sampleData = {
      contact: {
        first_name: previewData.contact?.first_name || 'John',
        last_name: previewData.contact?.last_name || 'Doe',
        email: previewData.contact?.email || 'john.doe@example.com',
        phone: previewData.contact?.phone || '+1234567890',
        company: previewData.contact?.company || 'Example Corp'
      },
      tenant: {
        name: previewData.tenant?.name || 'Your Company',
        domain: previewData.tenant?.domain || 'yourcompany.com'
      },
      campaign: {
        name: previewData.campaign?.name || 'Sample Campaign',
        description: previewData.campaign?.description || 'This is a sample campaign'
      },
      system: {
        unsubscribe_url: previewData.system?.unsubscribe_url || 'https://yourcompany.com/unsubscribe',
        company_name: previewData.system?.company_name || 'Your Company',
        rating_gate_link: previewData.system?.rating_gate_link || 'https://app.feedbackgate.com/rating-gate?campaign_id=sample&contact_id=sample'
      }
    };

    const renderContent = (content: string) => {
      if (!content) return '';
      
      // Replace variables with sample data
      let rendered = content;
      
      // Replace nested variables like {{contact.first_name}}
      rendered = rendered.replace(/\{\{contact\.first_name\}\}/g, sampleData.contact.first_name);
      rendered = rendered.replace(/\{\{contact\.last_name\}\}/g, sampleData.contact.last_name);
      rendered = rendered.replace(/\{\{contact\.email\}\}/g, sampleData.contact.email);
      rendered = rendered.replace(/\{\{contact\.phone\}\}/g, sampleData.contact.phone);
      rendered = rendered.replace(/\{\{contact\.company\}\}/g, sampleData.contact.company);
      
      rendered = rendered.replace(/\{\{tenant\.name\}\}/g, sampleData.tenant.name);
      rendered = rendered.replace(/\{\{tenant\.domain\}\}/g, sampleData.tenant.domain);
      
      rendered = rendered.replace(/\{\{campaign\.name\}\}/g, sampleData.campaign.name);
      rendered = rendered.replace(/\{\{campaign\.description\}\}/g, sampleData.campaign.description);
      
      rendered = rendered.replace(/\{\{system\.unsubscribe_url\}\}/g, sampleData.system.unsubscribe_url);
      rendered = rendered.replace(/\{\{system\.company_name\}\}/g, sampleData.system.company_name);
      rendered = rendered.replace(/\{\{system\.rating_gate_link\}\}/g, sampleData.system.rating_gate_link);
      
      return rendered;
    };

    // Add auto-appended content (same logic as backend)
    const addAutoAppendedContent = (content: string, templateType: string) => {
      let finalContent = content;
      
      // Check if rating gate link is already in content
      const hasRatingGateLink = finalContent.includes('{{system.rating_gate_link}}') || 
                                 finalContent.includes(sampleData.system.rating_gate_link);
      
      // Add rating gate link if not present (BEFORE opt-out message)
      if (!hasRatingGateLink) {
        if (templateType === 'email') {
          // Add HTML link for email
          const ratingLinkHtml = `
<div style="margin-top: 20px; padding: 15px; text-align: center;">
    <a href="${sampleData.system.rating_gate_link}" style="display: inline-block; padding: 12px 24px; background-color: #007bff; color: #ffffff; text-decoration: none; border-radius: 5px; font-weight: bold;">Rate Your Experience</a>
</div>`;
          finalContent = finalContent + ratingLinkHtml;
        } else {
          // Add plain text link for SMS
          const ratingLinkText = `\n\nRate your experience: ${sampleData.system.rating_gate_link}`;
          // Check SMS length limit (160 chars total)
          if (finalContent.length + ratingLinkText.length > 160) {
            const maxContentLength = 160 - ratingLinkText.length;
            finalContent = finalContent.substring(0, maxContentLength).trim();
          }
          finalContent = finalContent + ratingLinkText;
        }
      }
      
      // Add opt-out message (AFTER rating gate link)
      if (templateType === 'email') {
        // Check if unsubscribe footer already exists
        if (!finalContent.toLowerCase().includes('unsubscribe') && 
            !finalContent.includes('{{system.unsubscribe_url}}')) {
          const unsubscribeFooter = `
        <div style="margin-top: 20px; padding: 10px; border-top: 1px solid #ccc; font-size: 12px; color: #666;">
            <p>You received this email because you are subscribed to our communications.</p>
            <p>To unsubscribe from future emails, <a href="${sampleData.system.unsubscribe_url}">click here</a> or reply with STOP.</p>
            <p>For help, reply with HELP or contact us at support.</p>
        </div>
        `;
          finalContent = finalContent + unsubscribeFooter;
        }
      } else if (templateType === 'sms') {
        // Check if compliance text already exists
        if (!finalContent.toLowerCase().includes('stop') && 
            !finalContent.toLowerCase().includes('help')) {
          const complianceText = '\n\nReply STOP to unsubscribe or HELP for help.';
          // Ensure SMS doesn't exceed reasonable length
          if (finalContent.length + complianceText.length > 160) {
            const maxContentLength = 160 - complianceText.length;
            finalContent = finalContent.substring(0, maxContentLength).trim();
          }
          finalContent = finalContent + complianceText;
        }
      }
      
      return finalContent;
    };

    const renderedSubject = template.subject ? renderContent(template.subject) : null;
    let renderedContent = template.content ? renderContent(template.content) : '';
    
    // Add auto-appended content to show what will be sent
    if (renderedContent) {
      renderedContent = addAutoAppendedContent(renderedContent, template.template_type || 'email');
    }

    return (
      <div className="space-y-4">
        {renderedSubject && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Subject:</h4>
            <div className="p-3 bg-gray-50 rounded-md">
              {renderedSubject}
            </div>
          </div>
        )}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Content:</h4>
          {renderedContent ? (
            template.template_type === 'email' ? (
              <div 
                className="p-3 bg-gray-50 rounded-md prose max-w-none"
                dangerouslySetInnerHTML={{ __html: renderedContent }}
              />
            ) : (
              <div className="p-3 bg-gray-50 rounded-md whitespace-pre-wrap">
                {renderedContent}
              </div>
            )
          ) : (
            <div className="p-3 bg-gray-50 rounded-md text-gray-500 italic">
              No content to preview. Add content to see the preview.
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    // For unsaved templates (no templateId), use client-side preview
    if (!templateId) {
      return renderClientPreview();
    }

    // For saved templates, use server-side preview
    if (previewMutation.error) {
      return (
        <div className="p-3 bg-red-50 rounded-md text-red-700">
          <p className="font-medium">Error loading preview:</p>
          <p className="text-sm mt-1">
            {previewMutation.error instanceof Error 
              ? previewMutation.error.message 
              : 'Failed to generate preview. Please try again.'}
          </p>
        </div>
      );
    }

    if (previewMutation.data) {
      return (
        <div className="space-y-4">
          {previewMutation.data.rendered_subject && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Subject:</h4>
              <div className="p-3 bg-gray-50 rounded-md">
                {previewMutation.data.rendered_subject}
              </div>
            </div>
          )}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Content:</h4>
            {previewMutation.data.rendered_content ? (
              <div 
                className={`p-3 bg-gray-50 rounded-md ${template.template_type === 'email' ? 'prose max-w-none' : 'whitespace-pre-wrap'}`}
                dangerouslySetInnerHTML={template.template_type === 'email' ? { __html: previewMutation.data.rendered_content } : undefined}
              >
                {template.template_type === 'sms' && previewMutation.data.rendered_content}
              </div>
            ) : (
              <div className="p-3 bg-gray-50 rounded-md text-gray-500 italic">
                No content to preview.
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="p-3 bg-gray-50 rounded-md text-gray-500 italic">
        Click "Preview" to see how your template will look.
      </div>
    );
  };

  // Show loading state while fetching template data
  if (templateId && isLoadingTemplate) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="ml-3 text-sm text-gray-500">Loading template...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Back Button - Only show in edit mode and not in modal */}
      {templateId && !isModal && (
        <button
          onClick={() => navigate('/templates')}
          className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Templates
        </button>
      )}
      
      <TooltipProvider>
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold text-gray-900">
              {templateId ? 'Edit Template' : 'Create Template'}
            </h2>
            {(() => {
              const variablesHelp = getHelpContent('templateVariables');
              const bestPracticesHelp = getHelpContent('templateBestPractices');
              return variablesHelp ? (
                <HelpIcon
                  tooltip="Learn about template variables"
                  modalTitle={variablesHelp.title}
                  modalContent={
                    <div>
                      <p className="mb-4">{variablesHelp.content}</p>
                      {bestPracticesHelp && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-semibold mb-2">{bestPracticesHelp.title}</h4>
                          <p>{bestPracticesHelp.content}</p>
                        </div>
                      )}
                    </div>
                  }
                  size="md"
                />
              ) : null;
            })()}
          </div>
          <p className="text-gray-600">
            {templateId ? 'Update your template' : 'Create a new email or SMS template'}
          </p>
        </div>
      
      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Please fix the following errors:
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <ul className="list-disc pl-5 space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Name *
              </label>
              <input
                type="text"
                value={template.name || ''}
                onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter template name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type *
                </label>
                <select
                  value={template.template_type || 'email'}
                  onChange={(e) => setTemplate(prev => ({ ...prev, template_type: e.target.value as 'email' | 'sms' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={template.status || 'draft'}
                  onChange={(e) => setTemplate(prev => ({ ...prev, status: e.target.value as 'draft' | 'active' | 'archived' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={template.description || ''}
                onChange={(e) => setTemplate(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter template description"
              />
            </div>
          </div>

          {/* Email Subject (for email templates) */}
          {template.template_type === 'email' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={template.subject || ''}
                onChange={(e) => handleSubjectChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter email subject"
              />
            </div>
          )}

          {/* Content */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Content *
              </label>
              <div className="flex space-x-2">
                <button
                  onClick={handlePreview}
                  className="flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  <EyeIcon className="w-4 h-4 mr-1" />
                  Preview
                </button>
                {templateId && (
                  <button
                    onClick={handleValidate}
                    className="flex items-center px-3 py-1 text-sm text-green-600 hover:text-green-800"
                  >
                    <CheckIcon className="w-4 h-4 mr-1" />
                    Validate
                  </button>
                )}
              </div>
            </div>
            
            {template.template_type === 'email' ? (
              <EmailEditor
                value={template.content || ''}
                onChange={handleContentChange}
                placeholder="Enter email content..."
              />
            ) : (
              <textarea
                value={template.content || ''}
                onChange={(e) => handleContentChange(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter SMS content (max 160 characters)"
              />
            )}
            
            {template.template_type === 'sms' && (
              <div className="mt-1 text-sm text-gray-500">
                {template.content?.length || 0}/160 characters
              </div>
            )}
          </div>

          {/* Variables */}
          {template.variables && template.variables.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Variables
              </label>
              <div className="flex flex-wrap gap-2">
                {template.variables.map(variable => (
                  <span
                    key={variable}
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800"
                  >
                    {`{{${variable}}}`}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Preview/Validation */}
        <div className="space-y-6">
          {/* Template Variables Info */}
          <TemplateVariablesInfo />

          {previewMode && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Preview</h3>
              {previewMutation.isPending ? (
                <div className="text-center py-4">Loading preview...</div>
              ) : (
                renderPreview()
              )}
            </div>
          )}

          {validateMutation.data && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Validation</h3>
              <div className="space-y-2">
                {validateMutation.data.valid ? (
                  <div className="flex items-center text-green-600">
                    <CheckIcon className="w-5 h-5 mr-2" />
                    Template is valid
                  </div>
                ) : (
                  <div className="flex items-center text-red-600">
                    <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                    Template has issues
                  </div>
                )}
                
                {validateMutation.data.errors?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-red-700 mb-2">Errors:</h4>
                    <ul className="text-sm text-red-600 space-y-1">
                      {validateMutation.data.errors.map((error: string, index: number) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {validateMutation.data.warnings?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-yellow-700 mb-2">Warnings:</h4>
                    <ul className="text-sm text-yellow-600 space-y-1">
                      {validateMutation.data.warnings.map((warning: string, index: number) => (
                        <li key={index}>• {warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-6 border-t mt-6">
        <div>
          {/* Save as Copy button - Only show when editing */}
          {templateId && (
            <button
              onClick={handleSaveAsCopy}
              disabled={duplicateMutation.isPending || saveTemplateMutation.isPending}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
              {duplicateMutation.isPending ? 'Duplicating...' : 'Save as Copy'}
            </button>
          )}
        </div>
        
        <div className="flex space-x-4">
          <button
            onClick={handleCancel}
            disabled={saveTemplateMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saveTemplateMutation.isPending}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            {saveTemplateMutation.isPending && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {saveTemplateMutation.isPending ? 'Saving...' : (templateId ? 'Update Template' : 'Create Template')}
          </button>
        </div>
      </div>
      </TooltipProvider>
    </div>
  );
};
